import React, { useState, useEffect, useCallback, useContext } from 'react';
import { BrandingContext } from '../../../../BrandingProvider';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import FilterToolbar from '../../components/FilterToolbar';
import { getGrns, cancelGrn } from '../../api/grn';
import { getVendors } from '../../api/vendor';
import { getStores } from '../../api/stockLedger';
import usePoGrnLookup from '../../hooks/usePoGrnLookup';
import { formatDate, exportToCsv } from '../../utils';
import { isGrnPosted } from '../../utils/grnEntity';
import { grnStatusOf, canCancelGrn } from '../../utils/grnLifecycle';
import { buildGrnPdfPackage, openGrnPdfUrl, downloadGrnPdfUrl } from '../../utils/grnPdf';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export default function GrnList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const base = url.replace('/grns', '');
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();
  const [grns, setGrns] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const letterheadMenu = useLetterheadMenu();
  const { resolvePoNumber } = usePoGrnLookup();

  useEffect(() => {
    Promise.all([
      // API: GET /inventory/vendors/
      getVendors().catch(() => []),
      // API: GET /inventory/stores/
      getStores().catch(() => []),
    ]).then(([v, s]) => {
      setVendors(normalizeList(v));
      setStores(normalizeList(s));
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = {};
      if (search) p.grn_number = search;
      if (statusFilter) p.status = statusFilter;
      // API: GET /inventory/grns/
      const data = await getGrns(p);
      setGrns(normalizeList(data));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  function resolveStoreName(storeId) {
    if (!storeId) return '—';
    const s = stores.find(x => (x.store_id || x.id) === storeId);
    if (!s) return storeId;
    return `${s.store_name} (${s.store_code})`;
  }

  async function handleViewPdf(row, letterhead) {
    const grnId = row.grn_id || row.id;
    setPdfLoadingId(grnId);
    try {
      const scope = { tenant_id: row.tenant_id, hospital_id: row.hospital_id };
      const pkg = await buildGrnPdfPackage(grnId, scope, {
        vendorName:  resolveVendorName(row.vendor_id),
        storeName:   resolveStoreName(row.receiving_store_id),
        poNumber:    resolvePoNumber(row.purchase_order_id, row.po_number),
        facilityLogo,
        letterhead,
      });
      openGrnPdfUrl(pkg.url);
      URL.revokeObjectURL(pkg.url);
    } catch (e) {
      toast.error(e.message || 'Failed to generate PDF');
    } finally {
      setPdfLoadingId(null);
    }
  }

  async function handleDownloadPdf(row, letterhead) {
    const grnId = row.grn_id || row.id;
    setPdfLoadingId(grnId);
    try {
      const scope = { tenant_id: row.tenant_id, hospital_id: row.hospital_id };
      const pkg = await buildGrnPdfPackage(grnId, scope, {
        vendorName:  resolveVendorName(row.vendor_id),
        storeName:   resolveStoreName(row.receiving_store_id),
        poNumber:    resolvePoNumber(row.purchase_order_id, row.po_number),
        facilityLogo,
        letterhead,
      });
      downloadGrnPdfUrl(pkg.url, pkg.filename);
      URL.revokeObjectURL(pkg.url);
    } catch (e) {
      toast.error(e.message || 'Failed to generate PDF');
    } finally {
      setPdfLoadingId(null);
    }
  }

  async function confirmDelete() {
    if (!can(INV_ACTIONS.GRN_CREATE)) return;
    if (!deleteTarget) return;
    const grnId = deleteTarget.grn_id || deleteTarget.id;
    setDeleting(true);
    try {
      // API: DELETE /inventory/grns/{id}/ — soft-delete, sets status=CANCELLED
      await cancelGrn(grnId);
      setDeleteTarget(null);
      toast.success('GRN cancelled');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to cancel GRN');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  // Fixed chip width from the longest known/data label so every status badge aligns.
  // Seeded from the canonical enums (not the filter dropdown) so the width is stable before
  // data lands and independent of the dropdown's display spellings. Mirrors PoList.
  const statusLabels = [...new Set([
    ...['DRAFT', 'RECEIVED', 'QC_PENDING', 'QC_COMPLETED', 'POSTED_TO_STOCK', 'CANCELLED'],
    ...grns.map(r => r.posting_status || r.status).filter(Boolean).map(String),
  ].map(s => s.replace(/_/g, ' ')))];
  const statusChipMinWidth = Math.ceil(Math.max(...statusLabels.map(l => l.length)) * 8 + 24);
  const statusColumnWidth = Math.max(180, statusChipMinWidth + 28);

  const columns = [
    { field: 'grn_number', headerName: 'GRN', width: 160 },
    { field: 'purchase_order_id', headerName: 'PO', width: 130,
      valueGetter: p => resolvePoNumber(p.row.purchase_order_id, p.row.po_number) },
    { field: 'vendor_id', headerName: 'Vendor', width: 160,
      valueGetter: p => resolveVendorName(p.row.vendor_id) },
    { field: 'receiving_store_id', headerName: 'Store', width: 160,
      valueGetter: p => resolveStoreName(p.row.receiving_store_id) },
    { field: 'grn_date', headerName: 'GRN Date', width: 120, valueFormatter: p => formatDate(p.value) },
    { field: 'status', headerName: 'Status', width: statusColumnWidth,
      renderCell: p => <StatusChip status={p.row.posting_status || p.value} minWidth={statusChipMinWidth} /> },
    { field: 'created_at', headerName: 'Created', width: 120, valueFormatter: p => formatDate(p.value) },
    { field: 'actions', headerName: '', width: 180, sortable: false,
      renderCell: (p) => {
        const grnId = p.row.grn_id || p.row.id;
        const busy = pdfLoadingId === grnId;
        // A cancelled GRN is terminal — resuming it in the wizard would only 4xx.
        const canResume = !isGrnPosted(p.row.status, p.row.posting_status)
          && grnStatusOf(p.row) !== 'CANCELLED';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {canResume && can(INV_ACTIONS.GRN_CREATE) && (
              <Tooltip title="Continue GRN">
                <IconButton size="small"
                  onClick={() => history.push(`${base}/grn/new?grn_id=${grnId}`)}
                  sx={{ color: IPD_COLORS.primary }}>
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View GRN">
              <IconButton size="small"
                onClick={() => history.push(`${base}/grn/${grnId}`)}
                sx={{ color: IPD_COLORS.primary }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {can(INV_ACTIONS.GRN_CREATE) && canCancelGrn(p.row) && (
              <Tooltip title="Delete"><IconButton size="small"
                onClick={() => setDeleteTarget(p.row)}
                sx={{ color: IPD_COLORS.error.text }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            )}
            <Tooltip title="View PDF">
              <IconButton size="small" disabled={busy}
                onClick={(e) => letterheadMenu.open(e, (lh) => handleViewPdf(p.row, lh))}
                sx={{ color: IPD_COLORS.primary }}>
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton size="small" disabled={busy}
                onClick={(e) => letterheadMenu.open(e, (lh) => handleDownloadPdf(p.row, lh))}
                sx={{ color: IPD_COLORS.success.text }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } },
  ];

  return (
    <Box>
      <LetterheadMenu menu={letterheadMenu} label="Goods Receipt Note" />
      <PageHeader eyebrow="Material Management" title="Goods Receipt Notes"
        description={`${grns.length} GRNs`}
        actions={
          <>
            <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"
              onClick={() => exportToCsv('grns.csv', grns)}>Export</InvButton>
            {can(INV_ACTIONS.GRN_CREATE) && (
              <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${base}/grn/new`)}>New GRN</InvButton>
            )}
          </>
        }
      />


      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by GRN#..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          width="wide"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...['DRAFT', 'RECEIVED', 'QC_PENDING', 'QC_COMPLETED', 'POSTED_TO_STOCK', 'CANCELLED'].map(s => ({ value: s, label: s })),
          ]}
        />
      </FilterToolbar>

      <InvDataGrid fillHeight rows={grns} columns={columns} loading={loading} error={error} onRetry={load}
        getRowId={r => r.grn_id || r.id} emptyMessage="No GRNs found."
        emptyAction={can(INV_ACTIONS.GRN_CREATE)
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${base}/grn/new`)}>Create GRN</InvButton>
          : null} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete GRN"
        description={`Delete ${deleteTarget?.grn_number || 'this GRN'}? It will be cancelled and removed from the list.`}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
